/**
 * Day bucketing for schedules, with an explicit caller-supplied UTC offset.
 *
 * Match times are stored as instants (`timestamptz`). Slicing the ISO string
 * buckets them by their **UTC** date, which is correct for a UTC-ish club and
 * wrong for everyone else: for a UTC+8…+10 club a 08:30 local morning match is
 * `00:30Z` on the same date while the previous evening's 22:00 local match is
 * `14:00Z` on the date before — one competition day splits into two buckets.
 *
 * The fix is a *caller-supplied* offset in minutes east of UTC, never a tz
 * database lookup: the caller (the mobile app) already knows the offset it is
 * rendering in, so daylight saving is irrelevant here — the same instant read
 * at +600 and at +660 differs only because the caller said so. Offset `0`
 * reproduces the historical behaviour exactly (it returns the raw date prefix,
 * so a value that is not a parseable instant still buckets the way it always
 * did).
 */

/** Widest legal offset east or west of UTC, in minutes (UTC+14 / UTC-14 fits inside). */
export const TZ_OFFSET_MIN_MAX = 840;

/** True for an integer offset inside ±`TZ_OFFSET_MIN_MAX`. */
export function isValidTzOffsetMin(value: number): boolean {
  return Number.isInteger(value) && Math.abs(value) <= TZ_OFFSET_MIN_MAX;
}

/** `Z`, `+HH:MM`, `+HHMM` — anything that pins an ISO string to an instant. */
const HAS_OFFSET_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The `YYYY-MM-DD` day key of `iso` as seen `tzOffsetMin` minutes east of UTC.
 *
 * `tzOffsetMin = 0` (the default) returns the ISO date prefix verbatim — the
 * pre-offset behaviour, byte for byte. A timestamp with no offset designator
 * is read as UTC (never as the Node process's local time). An unparseable
 * value degrades to its first ten characters rather than throwing.
 */
export function dayKeyFor(iso: string, tzOffsetMin = 0): string {
  if (!tzOffsetMin) return iso.slice(0, 10);
  const ms = Date.parse(HAS_OFFSET_RE.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(ms)) return iso.slice(0, 10);
  return new Date(ms + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

/**
 * One `YYYY-MM-DD` formatter per IANA zone. Building an `Intl.DateTimeFormat` costs tens of
 * microseconds and a schedule read buckets hundreds of rows, so the formatter is built once per
 * zone and reused. A zone Intl rejects throws before anything is stored, so it keeps falling back.
 */
const zoneDayFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneDayFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = zoneDayFormatters.get(timeZone);
  if (!formatter) {
    // en-CA renders numeric dates as YYYY-MM-DD.
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    zoneDayFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * The `YYYY-MM-DD` day key of `iso` on the calendar of the IANA zone
 * `timeZone` — the counterpart of {@link dayKeyFor} for the case where the
 * zone is known (an `event.timezone`) rather than a caller-supplied offset, so
 * daylight saving is handled by Intl. Same degradation rules: an offset-less
 * timestamp is read as UTC; an unparseable value or a zone Intl rejects falls
 * back to the ISO date prefix rather than throwing.
 */
export function dayKeyInZone(iso: string, timeZone: string): string {
  const ms = Date.parse(HAS_OFFSET_RE.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(ms)) return iso.slice(0, 10);
  try {
    return zoneDayFormatter(timeZone).format(new Date(ms));
  } catch {
    return iso.slice(0, 10);
  }
}
